import React, { ChangeEvent, useState } from 'react';
import {
  Tab,
  TabList,
  Tabs,
  TabPanels,
  TabPanel,
  Thead,
  Th,
  Tr,
  Tbody,
  Td,
  Alert,
  Center,
  Spinner,
} from '@chakra-ui/react';
import { Flex, Stack, Text } from '@chakra-ui/layout';
import { useEquipamentsSuggestion } from '~/hooks/useEquipmentsSuggestion';
import { Select, Table } from '~/components';
import { Card } from '~/components/Card';

interface EquipmentSuggestionProps {
  id_contrato: string | number | Number;
  showPageSize?: boolean;
  showAlert?: boolean;
}

export const EquipmentSuggestion: React.FC<EquipmentSuggestionProps> = ({
  id_contrato,
  showPageSize = false,
  showAlert = false,
}: EquipmentSuggestionProps) => {
  const [qtdeSugestoes, setQtdeSugestoes] = useState<number>(3);

  const { data, isLoading, error } = useEquipamentsSuggestion(
    id_contrato as any,
    qtdeSugestoes,
  );

  const handleQtdeSugestoesChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const quantidade = Number(event.target.value);

    if (quantidade) {
      setQtdeSugestoes(quantidade);
    }
  };

  return (
    <>
      {isLoading && (
        <Flex justify="center" py={20}>
          <Spinner />
        </Flex>
      )}
      {data?.length === 0 && !isLoading && (
        <Flex justify="center">
          <Card
            content={
              <Text align="center" padding={4}>
                O contrato não possui equipamentos.
              </Text>
            }
            width="100%"
          />
        </Flex>
      )}
      {error && !isLoading && (
        <Flex justify="center">
          <Card
            content={
              <Text align="center" padding={4}>
                Desculpe-nos, não há sugestões de equipamentos disponíveis no
                momento para este contrato.
              </Text>
            }
            width="100%"
          />
        </Flex>
      )}
      {data && data.length > 0 && !isLoading && (
        <Tabs variant="enclosed-colored">
          <TabList>
            {data?.map(item => (
              <Tab>
                <Text lineHeight={1}>
                  <span style={{ fontSize: 13, fontWeight: 500 }}>
                    {item.atual.codigo}
                  </span>
                  <br />
                  <span style={{ fontSize: 11 }}>
                    {item.atual.tipo_equipamento}
                  </span>
                </Text>
              </Tab>
            ))}
          </TabList>
          <TabPanels>
            {data?.map(item => (
              <TabPanel
                bgColor="#FFF"
                border="1px solid var(--chakra-colors-gray-200)"
              >
                <Text mb={2} fontSize={13}>
                  <span style={{ fontWeight: 500, marginRight: 5 }}>
                    Modelo:
                  </span>
                  {item.atual.modelo}
                </Text>
                <Text mb={2} fontSize={13}>
                  <span style={{ fontWeight: 500, marginRight: 5 }}>
                    Descrição do equipamento atual:
                  </span>
                  {item.atual.configuracao}
                </Text>
                <Text mb={2} fontSize={13} fontWeight={500}>
                  Sugestão:
                </Text>
                <Table type="secondary" variant="striped" size="sm" mb={3}>
                  <Thead>
                    <Tr>
                      <Th>Código</Th>
                      <Th>Tipo</Th>
                      <Th>Modelo</Th>
                      <Th>Descrição</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {item.sugestoes.length === 0 && (
                      <Tr>
                        <Td
                          colSpan={4}
                          border="1px solid var(--chakra-colors-gray-200)"
                        >
                          <Center fontWeight={500}>
                            Não foi possível obter sugestões para este
                            equipamento
                          </Center>
                        </Td>
                      </Tr>
                    )}
                    {item.sugestoes.length > 0 &&
                      item.sugestoes.map(sugestao => (
                        <Tr>
                          <Td>{sugestao.codigo}</Td>
                          <Td>{sugestao.tipo_equipamento}</Td>
                          <Td>{sugestao.modelo}</Td>
                          <Td>{sugestao.configuracao}</Td>
                        </Tr>
                      ))}
                  </Tbody>
                </Table>
                {showPageSize && (
                  <Flex mb={5} flexDir="row" gridGap={5} alignItems="center">
                    <Stack ml="auto">
                      <Text fontSize={13}>Quantidade de sugestões</Text>
                    </Stack>
                    <Stack>
                      <Select
                        fontSize={13}
                        name="qtdeSugestoes"
                        onChange={handleQtdeSugestoesChange}
                        defaultValue={qtdeSugestoes}
                      >
                        <option key={1} value={1}>
                          1
                        </option>
                        <option key={2} value={2}>
                          2
                        </option>
                        <option key={3} value={3}>
                          3
                        </option>
                        <option key={4} value={4}>
                          4
                        </option>
                        <option key={5} value={5}>
                          5
                        </option>
                      </Select>
                    </Stack>
                  </Flex>
                )}
                {showAlert && (
                  <Alert
                    display="block"
                    bg="#f7f2fa"
                    borderRadius="md"
                    color="#000"
                    fontSize="sm"
                    py={2}
                    px={3}
                    mb={5}
                  >
                    <span
                      style={{
                        fontWeight: 500,
                        marginRight: 5,
                        color: '#9246ce',
                      }}
                    >
                      Atenção:
                    </span>
                    Código de acordo com a tabela vigente. O mesmo deverá ser
                    validado com a área de administração de vendas
                  </Alert>
                )}
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      )}
    </>
  );
};
